import { StepAction } from './stepAction';
import type { ISerializedStep } from 'clickim/src/common/models/step';
import type { ScrollStep } from 'clickim/src/common/models/scrollStep';
import type { ILocatedElement } from 'clickim/src/background/codeSnippets/getLocatedElement';

export class ScrollStepAction extends StepAction<ScrollStep> {
    private getFailureString(step: ISerializedStep<ScrollStep>, expectedX: number, expectedY: number, actualX: number, actualY: number) {
        if (!step.isScrollToElement) {
            return `Scrolling limit reached. Expected:(y: ${expectedY}, x: ${expectedX}); Actual:(y:${actualY}, x: ${actualX})`;
        }
        let failureMessage = 'Scrolling limit reached';
        if (step.shouldScrollTop) {
            failureMessage += `. Expected top margin: ${expectedY}, actual: ${actualY}`;
        }
        if (step.shouldScrollLeft) {
            failureMessage += `. Expected left margin: ${expectedX}, actual: ${actualX}`;
        }
        return failureMessage;
    }

    private async scroll(elementToScrollTo: ILocatedElement | null, step: ISerializedStep<ScrollStep>, elementToScrollOn?: ILocatedElement) {
        const { codeSnippets, commonConstants: { stepResult } } = this.sessionPlayerInit;
        const expectedY = Math.round(Number(step.isScrollToElement ? step.marginTop : step.y));
        const expectedX = Math.round(Number(step.isScrollToElement ? step.marginLeft : step.x));

        // in Firefox setting scrollTop and scrollLeft properties simultaneously takes only the latest,
        const elementScrollTo = this.driver.isFirefox() ? function (element: Element, x: number, y: number) {
            element.scrollTo(x, y);
        } : function (element: Element, x: number, y: number) {
            element.scrollTop = y;
            element.scrollLeft = x;
        };
        const { scroll } = await import('./scripts/scroll');
        const scrollCode = `
            const getLocatedElement = ${codeSnippets.getLocatedElementCode};
            const elementScrollTo = ${elementScrollTo.toString()};
            const scroll = ${scroll.toString()};
            return scroll.apply(null, arguments)
        `;

        try {
            const res = await this.driver.executeJS<typeof scroll>(
                scrollCode,
                elementToScrollOn,
                elementToScrollTo ?? undefined,
                Boolean(step.isScrollToElement),
                Boolean(step.isDynamicScroll),
                expectedX,
                expectedY,
                step.shouldScrollLeft,
                step.shouldScrollTop,
            );
            if (!res?.value) {
                return {
                    errorType: stepResult.SCROLL_ACTION_FAILURE,
                    success: false,
                };
            }

            const { success, actualX, actualY } = res.value;

            if (success) {
                return { success: true };
            }

            return {
                errorType: stepResult.SCROLL_ACTION_FAILURE,
                success: false,
                resultInfo: { error: this.getFailureString(step, expectedX, expectedY, actualX!, actualY!) },
            };
        } catch (error) {
            return {
                errorType: stepResult.SCROLL_ACTION_FAILURE,
                success: false,
            };
        }
    }

    private scrollOnDocument(step: ISerializedStep<ScrollStep>, elementToScrollTo: ILocatedElement | null) {
        return this.scroll(elementToScrollTo, step);
    }

    private scrollOnElement(step: ISerializedStep<ScrollStep>, elementToScrollTo: ILocatedElement | null) {
        return this.scroll(elementToScrollTo, step, this.getTarget().locatedElement);
    }

    override execute() {
        const { context, step } = this;
        const elementToScrollTo = step.isScrollToElement ? context.data.scrollToElement.locatedElement : null;

        return step.element.isDocument ?
            this.scrollOnDocument(step, elementToScrollTo) :
            this.scrollOnElement(step, elementToScrollTo);
    }
}
